import { useEffect, useRef, useState } from 'react';
import { Outlet, NavLink } from 'react-router-dom';
import { signOut, useMirrorUser } from '@/lib/firebase';
import { PRODUCT_NAME, PRODUCT_NAME_SHORT, STUDIO_NAME, STUDIO_URL } from '@/lib/brand';

export default function Layout() {
  const { tokens, loading: tokensLoading } = useMirrorUser();
  const [accountOpen, setAccountOpen] = useState(false);
  const accountRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!accountOpen) return;
    const handleClickOutside = (e: MouseEvent) => {
      if (accountRef.current && !accountRef.current.contains(e.target as Node)) {
        setAccountOpen(false);
      }
    };
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setAccountOpen(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleEscape);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEscape);
    };
  }, [accountOpen]);

  return (
    <div className="flex min-h-screen flex-col bg-mirror-bg text-mirror-text">
      <header className="flex fixed w-full items-center gap-6 border-b border-mirror-border bg-mirror-bg/65 backdrop-blur-lg px-6 py-3 z-20">
        <NavLink to="/" className="font-display text-xl font-semibold tracking-wide text-mirror-gold no-underline hover:text-mirror-gold-hover transition-colors" title={`${PRODUCT_NAME} – a ${STUDIO_NAME} product`}>
          {PRODUCT_NAME_SHORT}
        </NavLink>
        <nav className="flex gap-3 justify-center flex-grow">
            <NavLink
                to="/"
                end
                className={({ isActive }) =>
                    `rounded-full px-2.5 py-1.5 text-sm no-underline transition-colors ${
                        isActive ? 'font-medium text-mirror-gold' : 'text-mirror-muted hover:bg-mirror-surface hover:text-mirror-text'
                    }`
                }
            >
                Create
            </NavLink>
            <NavLink
                to="/billing"
                className={({ isActive }) =>
                    `flex items-center gap-2 rounded-full border no-underline transition-all ${
                        isActive
                            ? 'border-mirror-gold bg-mirror-gold/20 text-mirror-gold'
                            : 'border-mirror-border bg-mirror-gold/90 text-mirror-text hover:border-mirror-gold/60 hover:bg-mirror-gold/10'
                    } px-3 py-1 min-w-[4.5rem] justify-center relative`
                }
                title="Token balance — click to top up"
            >
                <div className={"absolute w-full h-full bg-white/20 animate-pulse rounded-full"} />
                <span className="text-[0.65rem] uppercase tracking-wider text-black/80">Tokens</span>
                <span className="font-bold tabular-nums text-black">
              {tokensLoading ? '…' : tokens !== null ? tokens.toLocaleString() : '—'}
            </span>
            </NavLink>
          <NavLink
            to="/feed"
            end
            className={({ isActive }) =>
              `rounded-full px-2.5 py-1.5 text-sm no-underline transition-colors ${
                isActive ? 'font-medium text-mirror-gold' : 'text-mirror-muted hover:bg-mirror-surface hover:text-mirror-text'
              }`
            }
          >
            Get Inspired
          </NavLink>
        </nav>
        <div className="ml-auto flex gap-3 items-center" ref={accountRef}>
          <div className="relative">
            <button
              type="button"
              onClick={() => setAccountOpen((o) => !o)}
              className="flex items-center gap-2 rounded-lg border border-mirror-border bg-mirror-surface px-3 py-1.5 text-sm font-medium text-mirror-text transition-colors hover:bg-mirror-border hover:text-mirror-text"
              aria-expanded={accountOpen}
              aria-haspopup="true"
            >
              Account
              <span className={`text-mirror-muted transition-transform ${accountOpen ? 'rotate-180' : ''}`}>▾</span>
            </button>
            {accountOpen && (
              <div
                className="absolute right-0 top-full z-30 mt-1 min-w-[10rem] rounded-lg border border-mirror-border bg-mirror-card py-1 shadow-lg"
                role="menu"
              >
                <a
                  href="https://switchfit.xyz"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="block px-3 py-2 text-sm text-mirror-text no-underline transition-colors hover:bg-mirror-surface hover:text-mirror-gold"
                  role="menuitem"
                  onClick={() => setAccountOpen(false)}
                >
                  Go to SwitchFit
                </a>
                <button
                  type="button"
                  className="block w-full px-3 py-2 text-left text-sm text-mirror-text transition-colors hover:bg-mirror-surface hover:text-red-300"
                  role="menuitem"
                  onClick={() => {
                    setAccountOpen(false);
                    signOut();
                  }}
                >
                  Sign out
                </button>
              </div>
            )}
          </div>
        </div>
      </header>
      <main className="flex-1 p-6 pt-20">
        <Outlet />
      </main>
      <footer className="border-t border-mirror-border px-6 py-3 text-center text-sm text-mirror-muted">
        {PRODUCT_NAME} is a product of{' '}
        <a href={STUDIO_URL} target="_blank" rel="noopener noreferrer" className="text-mirror-gold no-underline hover:underline">
          {STUDIO_NAME}
        </a>
        {' · '}
        <NavLink to="/privacy" className="text-mirror-gold no-underline hover:underline">
          Privacy
        </NavLink>
        {' · '}
        <NavLink to="/terms" className="text-mirror-gold no-underline hover:underline">
          Terms
        </NavLink>
      </footer>
    </div>
  );
}
